import semver from 'semver';
import { isNumber } from 'lodash-es';
import { message as EeveeMessage } from '@fuxi/eevee-ui';
import { TaskQueryKey } from 'common/constants';
import { getNormalParams, FastApiAxiosInstance } from 'common/service/axiosInstances';
import { TaskStatus, ParentTask, Task, TaskSettlementDetail, TaskScene, WorkStyleType } from 'common/types';

const tasks = {
  getTaskVersion: async ({ parentTaskId, taskId }: { parentTaskId?: number; taskId?: number }) => {
    if (!parentTaskId && !taskId) {
      return;
    }
    const params = { ...getNormalParams('getTaskVersion') };
    taskId && Object.assign(params, { taskId: Number(taskId) });
    parentTaskId && Object.assign(params, { parentTaskId: Number(parentTaskId) });
    const { data } = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/task/getTaskVersion', { params });
    let isVersion0_3_x = false;
    try {
      isVersion0_3_x = semver.gt(data.version, '0.2.3');
    } catch (e) {}
    return isVersion0_3_x;
  },
  getTaskNumOverview: async (role, projectId?: number) => {
    const params = { ...getNormalParams('getTaskNumOverview'), role };
    !!projectId && Object.assign(params, { projectId });
    const { data } = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/task/getTaskNumOverview', {
      params,
    });
    // data 格式[{status:'', num: 3 }]
    const statusCounts = {
      [TaskStatus.AllStatus]: 0,
      [TaskStatus.Processing]: 0,
      [TaskStatus.End]: 0,
      [TaskStatus.Accepted]: 0,
      [TaskStatus.Settling]: 0,
    };
    data.forEach(item => {
      const total = isNumber(item?.num)
        ? statusCounts[TaskStatus.AllStatus] + item?.num
        : statusCounts[TaskStatus.AllStatus];
      Object.assign(statusCounts, { [item?.status]: item?.num, [TaskStatus.AllStatus]: total });
    });
    return statusCounts;
  },
  // 父任务的详情页
  // getParentTaskInfo: async ({ parentTaskId, status }: { parentTaskId: number; status?: TaskStatus[] }) => {
  //   const params = { ...getNormalParams('getParentTaskInfo'), parentTaskId, status: status?.join(',') };
  //   const { data } = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/task/getParentTaskInfo', {
  //     params,
  //   });
  //   return data;
  // },
  // 子任务的任务信息面板的展示数据
  getTaskInfo: async ({ taskId, ignoreErrorMsg }: { taskId: number; ignoreErrorMsg?: boolean }): Promise<Task> => {
    const params = { ...getNormalParams('getTaskInfo'), taskId };
    const { data } = await FastApiAxiosInstance.get('/api/v1/task/getTaskInfo', { params });
    const { taskTemplate: originTaskTemplate } = data || {};
    let taskTemplateObj;
    if (originTaskTemplate) {
      try {
        taskTemplateObj = JSON.parse(originTaskTemplate);
      } catch (e) {}
    }
    return { ...data, taskTemplateObj };
  },

  // 更新父任务列表的单项数据
  updateParentTask: async (parentTaskId, preview) => {
    const { data, code, message } = await FastApiAxiosInstance.post<any, ExtendAxioResponse>(
      '/api/v1/task/updateParentTask',
      { ...getNormalParams('updateParentTask'), parentTaskId, preview },
    );
    const hasError = code !== 0;
    if (hasError) {
      EeveeMessage.error(message || '删除反馈失败');
    } else {
      EeveeMessage.success('成功');
    }
    return code === 0;
  },

  // 更新任务列表的单项数据
  addTaskAttachment: async (parentTaskId, url: string, fileName: string, fileSize: number) => {
    const { data, code, message } = await FastApiAxiosInstance.post<any, ExtendAxioResponse>(
      '/api/v1/task/addTaskAttachment',
      { ...getNormalParams('addTaskAttachment'), parentTaskId, url, fileName, fileSize },
    );

    return code === 0;
  },
  getParentTaskIMGroupId: async (parentTaskId: number) => {
    return FastApiAxiosInstance.get('/api/v1/message/im/getGroupId', { params: { parentTaskId } });
  },
  //获取结算明细
  getTaskSettlementDetail: async parentTaskId => {
    return FastApiAxiosInstance.get<TaskSettlementDetail>('/api/v1/task/settlement/get', { params: { parentTaskId } });
  },
  //获取收款方列表
  getPayeeList: async parentTaskId => {
    return FastApiAxiosInstance.post<any, ExtendAxioResponse>('/api/v1/task/settlement/assign', { parentTaskId });
  },
  //需求方指派收款方，确认结算
  assignPayee: async (parentTaskId, externalAccountId) => {
    return FastApiAxiosInstance.post<any, ExtendAxioResponse>('/api/v1/task/settlement/begin', {
      parentTaskId,
      externalAccountId,
    });
  },
  //制作方确认结算
  confirmSettlement: async parentTaskId => {
    return FastApiAxiosInstance.post<any, ExtendAxioResponse>('/api/v1/task/settlement/agree', { parentTaskId });
  },
  // 获取任务场景，包含场景下的类型
  getTaskSceneList: () => {
    return FastApiAxiosInstance.get<{ total: number; items: TaskScene[] }>('/api/v2/task/requirement/scene');
  },

  //新版getTasks
  getTasksNew: async ({ _page, _num, _query, listStatus, projectId, role, onlyUncheck, stageStatus }) => {
    const status = listStatus;

    const params = { ...getNormalParams('getTasks'), _page, _num, _query, status, role, onlyUncheck, stageStatus };
    !!projectId && Object.assign(params, { projectId });
    const {
      data: { items, total },
    } = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v2/task/getTasks', { params });
    return { items, total } as { items: ParentTask[]; total: number };
  },
  //新版获取任务overview
  getTaskNumOverviewNew: async (role, projectId?: number) => {
    const params = { ...getNormalParams('getTaskNumOverview'), role };
    !!projectId && Object.assign(params, { projectId });
    const { data } = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v2/task/getTaskNumOverview', {
      params,
    });
    // data 格式[{status:'', num: 3 }]
    // const statusCounts = {
    //   [TaskStatus.Bidding]: 0,
    //   [TaskStatus.Processing]: 0,
    //   [TaskStatus.End]: 0,
    //   [TaskStatus.Accepted]: 0,
    //   [TaskStatus.Settling]: 0,
    // };
    // data.forEach(item => {
    //   Object.assign(statusCounts, { [item?.status]: item?.total });
    // });

    // return statusCounts;
    return data;
  },
};

export default tasks;
